import { useEffect, useRef, useState } from "react";
import { NavigationType, useBlocker } from "react-router-dom";

export const LEAVE_WITHOUT_GUARD = { skipUnsavedGuard: true } as const;

function skipsGuard(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { skipUnsavedGuard?: unknown }).skipUnsavedGuard === true
  );
}

function releasesGuard(action: NavigationType, state: unknown): boolean {
  if (action === NavigationType.Pop) return false;
  return skipsGuard(state);
}

export function useUnsavedChangesGuard(isDirty: boolean, onLeave: () => void) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) =>
      isDirty &&
      currentLocation.pathname !== nextLocation.pathname &&
      !releasesGuard(historyAction, nextLocation.state),
  );
  const navigationBlocked = blocker.state === "blocked";
  const proceeding = useRef(false);

  useEffect(() => {
    if (navigationBlocked) proceeding.current = false;
  }, [navigationBlocked]);

  const requestLeave = () => {
    if (isDirty) {
      setConfirmOpen(true);
      return;
    }
    onLeave();
  };

  const confirmLeave = () => {
    setConfirmOpen(false);
    if (navigationBlocked) {
      proceeding.current = true;
      blocker.proceed();
      return;
    }
    onLeave();
  };

  const cancelLeave = () => {
    if (proceeding.current) return;
    setConfirmOpen(false);
    if (navigationBlocked) blocker.reset();
  };

  return {
    confirmOpen: confirmOpen || navigationBlocked,
    requestLeave,
    confirmLeave,
    cancelLeave,
  };
}
